import type {VoidFunctionComponent} from "react";
import React from "react";
import {useSongResultsHomeLogic} from "./SongsResultsHome.logic";
import {FeaturedCard} from "../../../components/cards/featuredCard/FeaturedCard";
import {CardSlider} from "../../../components/cardSlider/CardSlider";
import s from "./SongsResultsHome.module.scss";
import {IPlaylistCardProps, PlaylistCard} from "../../../components/cards/playlistCard/PlaylistCard";
import {ISongCard, SongCard} from "../../../components/common/song-card/SongCard";
import {LibraryLessonCard} from "../../../components/cards/library-lesson-card/LibraryLessonCard";
import {
  MusicHomeCard,
  MusicHomePlaylistCard,
  MusicHomeSongCard
} from "../../../client/models/musicHome/MusicHomeResponse";
import Button from "../../../components/common/Button";
import {useAppSelector} from "../../../redux/hooks";
import ZeroHeightLoadingAnimation2 from "../../../components/loadingAnimation/ZeroHeightLoadingAnimation2";
import {musicHomeStore} from "../../../pages/songs-router/modules/music-home-store/musicHomeStore";

export interface ISongResultsHomeProps {
}

function calculateKey(card: MusicHomeCard): string {
  switch (card.type) {
    case "song":
      return `${card.type} ${card.songId}`;
    case "lesson":
      return `${card.type} ${card.lessonId}`;
    case "playlist":
      return `${card.type} ${card.playlistId}`;
  }
}

function calculateSongCardProps(card: MusicHomeSongCard): ISongCard {
  return {
    artistName: card.artistName,
    songId: card.songId,
    thumbnailUrl: card.imageUrl,
    name: card.title,
    hasVideo: card.hasVideo,
    lessonCount: card.numberOfArrangements,
  };
}

function calculatePlaylistCardProps(card: MusicHomePlaylistCard, isOnlyPlaylists: boolean): IPlaylistCardProps {
  const difficultyIdentifier = card.difficultyIdentifier;
  return {
    title: card.title,
    subtext: card.subtext,
    difficultyIdentifier: difficultyIdentifier !== "mixed" ? difficultyIdentifier : undefined,
    imgUrl: card.imageUrl,
    isDisplayedInGroup: !isOnlyPlaylists,
    playlistId: card.playlistId,
  };
}

export const SongsResultsHome: VoidFunctionComponent<ISongResultsHomeProps> = ({}) => {
  const {
    isLoading,
    musicHomeResponse,
    selectedGroupId,
    seeAll,
    backToMusicHome,
    handleSwitchSearchEngines,
    calculateFeaturedCardProps,
    calculateLessonCardProps,
  } = useSongResultsHomeLogic();

  const searchTerm = useAppSelector(musicHomeStore.getSearchText);

  const loadingEl = isLoading ? <ZeroHeightLoadingAnimation2/> : null;

  return <div>
    {loadingEl}
    {
      musicHomeResponse.groups.length === 0 && searchTerm.length > 0
        ?
        <div className={s.noMatchesTextContainer}>
          <div className={s.noMatchesText}>
            No matches in Home. Would you like to search the entire Songs collection?
            <Button
              text={`Search`}
              buttonType={"secondary"}
              onClick={handleSwitchSearchEngines}
              navigationNote={"D"}
              additionalClassName={s.searchButton}
            />
          </div>
        </div>
        :
        <>
          {musicHomeResponse.groups.map((group, index) => {
            const isOnlyPlaylists = group.cards.every((card) => card.type === "playlist");
            const isSelectedGroup = group.id === selectedGroupId;

            return <CardSlider hasHeader={!!group.title}
              headerTitle={group.title}
              key={group.id}
              seeAll={() => seeAll(group.id)}
              isSelected={group.id === selectedGroupId}
              backToMusicHome={backToMusicHome}
            >
              {
                group.cards.map((card) => {
                  if (group.isFeatured) {
                    return <FeaturedCard {...calculateFeaturedCardProps(card)} key={calculateKey(card)}/>;
                  }

                  switch (card.type) {
                    case "song":
                      return <SongCard {...calculateSongCardProps(card)} key={calculateKey(card)}/>;
                    case "lesson":
                      return <LibraryLessonCard {...calculateLessonCardProps(card)} key={calculateKey(card)}/>;
                    case "playlist":
                      return <PlaylistCard {...calculatePlaylistCardProps(card, isOnlyPlaylists)} key={calculateKey(card)} isDisplayedInGroupContents={isSelectedGroup}/>;
                  }
                })
              }
            </CardSlider>;
          })}
        </>
    }
  </div>;
}

